import { useState } from "react";
import { motion } from "motion/react";
import styled from 'styled-components';

const SelectContainer = styled.div`
  position: relative;
  margin-bottom: 2rem;
  width: 100%;
  padding-top: 1.5rem;
`;

const AnimatedLabel = styled(motion.div)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  left: 0;
  z-index: 10;
`;

const AnimatedLetter = styled(motion.span)`
  display: inline-block;
  font-size: 0.875rem;
  will-change: transform;
  color: #333333;
`;

const StyledAnimatedSelect = styled.select`
  outline: none;
  border: none;
  border-bottom: 2px solid ${props => props.error ? props.theme.colors.error : props.theme.colors.border};
  padding: 0.5rem 2rem 0.5rem 0;
  width: 100%;
  font-size: 1rem;
  font-weight: 500;
  background: transparent;
  color: ${props => props.theme.colors.text};
  appearance: none;
  transition: border-color 0.2s ease;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23757575' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0 center;
  background-size: 16px;
  
  &:focus {
    border-bottom-color: ${props => props.error ? props.theme.colors.error : props.theme.colors.primary};
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const containerVariants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const letterVariants = {
  initial: {
    y: 0,
    color: "#333333", // Use a concrete color instead of "inherit"
  },
  animate: {
    y: "-250%",
    color: "#757575", // gray color
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 20,
    },
  },
};

export const AnimatedSelect = ({
  label,
  value,
  options = [],
  placeholder,
  error,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const showLabel = isFocused || (value && value.length > 0);

  return (
    <SelectContainer>
      <AnimatedLabel
        variants={containerVariants}
        initial="initial"
        animate={showLabel ? "animate" : "initial"}
      >
        {label.split("").map((char, index) => (
          <AnimatedLetter
            key={index}
            variants={letterVariants}
          >
            {char === " " ? "\u00A0" : char}
          </AnimatedLetter>
        ))}
      </AnimatedLabel>

      <StyledAnimatedSelect
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        value={value}
        error={error}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </StyledAnimatedSelect>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </SelectContainer>
  );
};